import useBridge from "@/hooks/useBridge";
import { useCallback, useEffect, useRef, useState } from "react";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import {
  useContractWrite,
  useSwitchNetwork,
  useWaitForTransaction,
  useNetwork,
} from "wagmi";
import { waitForTransaction } from "wagmi/actions";
import {
  mintValidatorPublic,
  mintValidatorPrivate,
} from "@/services/network/validator";
import useAccount from "@/hooks/useAccount";
import stores, { actions, useTypedSelector } from "@/stores";
import ValidatorNFT from "@/chain-info/contracts/ValidatorNFT.json";
import { zksyncChainId } from "@/config/config";
import { parseEther } from "viem";
import { EscrowDeposit, WhiteListInfo } from "@/types/network";
import useMintHistory from "@/hooks/useMintHistory";
import useRequest from "@/hooks/useRequest";
import useValidatorContract from "@/hooks/useValidatorContract";
import useMintType from "@/hooks/useMintType";
import useMintResult from "@/hooks/useMintResult";
import * as Sentry from "@sentry/nextjs";
import mixpanel from "mixpanel-browser";

export default function useMintValidator() {
  const { address, isLogin, login } = useAccount();
  const { chain } = useNetwork();
  const { depositSuccess, depositing, karatDeposit, zkSyncDeposit } =
    useBridge();
  const [minting, setMinting] = useState(false);
  const { switchNetworkAsync } = useSwitchNetwork();
  const { form } = useVerifyClaim();
  const { history: mintHistory, status: mintStatus } = useMintHistory();
  const mintType = useMintType();
  const [mintId, setMintId] = useState<string | undefined>();
  const mintResultRef = useRef<{
    timer: any;
    result: any;
    success: boolean;
    error: any;
  }>({
    timer: null,
    result: null,
    success: false,
    error: null,
  });

  const {
    data: mintResult,
    isSuccess: isMintSuccess,
    error: mintError,
  } = useMintResult(mintId);

  const validatorContractInfo = useValidatorContract(zksyncChainId);

  const validatorContract = useTypedSelector((state) => {
    return state.devtool.config.validatorContract;
  });

  useEffect(() => {
    mintResultRef.current.result = mintResult;
    mintResultRef.current.success = isMintSuccess;
    mintResultRef.current.error = mintError;
  }, [mintResult, isMintSuccess, mintError]);

  useEffect(() => {
    return () => {
      mintResultRef.current.timer && clearInterval(mintResultRef.current.timer);
    };
  }, []);

  const { data: whiteListInfo, error: whiteListError } =
    useRequest<WhiteListInfo>(
      mintType === "validator" && address && validatorContractInfo.stage === 1
        ? {
            function: "validator/getWhiteListInfo",
            data: {
              walletAddress: address,
            },
            header: {
              endpoint: "network",
            },
          }
        : null
    );

  useEffect(() => {
    if (!address) {
      return;
    }
    if (
      ["0x89af1676d510f0a037cf85f7f175f5e50154c9ed"].includes(
        address.toLowerCase()
      ) ||
      whiteListError
    ) {
      if (whiteListError || whiteListInfo) {
        Sentry.captureEvent({
          message: "whitelist debug",
          extra: {
            address,
            error: whiteListError,
            info: whiteListInfo,
          },
        });
      }
    }
  }, [whiteListInfo, address, whiteListError]);

  const { writeAsync: mintValidatorPublicByContract, data: publicMintData } =
    useContractWrite({
      address: validatorContract as any,
      abi: ValidatorNFT.abi,
      functionName: "mintValidatorPublic",
      chainId: zksyncChainId,
      args: [address, 2],
      gas: 40000000n,
      value: parseEther(String(0.2)),
    });

  const { writeAsync: mintValidatorPrivateByContract, data: mintData } =
    useContractWrite({
      address: validatorContract as any,
      abi: ValidatorNFT.abi,
      functionName: "mintValidatorPrivate",
      chainId: zksyncChainId,
      args: [
        address,
        parseInt(whiteListInfo?.tier || "2"),
        parseEther(whiteListInfo?.mintPrice || "0"),
        whiteListInfo?.proof,
      ],
      gas: 80000000n,
      value: parseEther(whiteListInfo?.mintPrice || "0"),
    });

  const { refetch: fetchTransaction } = useWaitForTransaction({
    hash: mintData?.hash,
    chainId: zksyncChainId,
  });

  const { refetch: fetchPublicTransaction } = useWaitForTransaction({
    hash: publicMintData?.hash,
    chainId: zksyncChainId,
  });

  const checkWhiteList = useCallback(() => {
    if (mintType !== "validator") {
      return;
    }
    if (validatorContractInfo.stage === 1 && whiteListError) {
      throw new Error(whiteListError.message);
    }
    if (validatorContractInfo.stage === 0) {
      throw new Error("Mint closed");
    }
  }, [whiteListError, mintType, validatorContractInfo]);

  const contractMint = useCallback(async () => {
    setMinting(true);
    console.log("start contract mint");
    try {
      if (!isLogin) {
        login();
        return false;
      }
      checkWhiteList();
      if (chain?.id !== zksyncChainId) {
        // @ts-ignore
        await switchNetworkAsync(zksyncChainId as number);
      }
      let res;
      if (validatorContractInfo?.stage === 1) {
        res = await mintValidatorPrivateByContract();
      } else {
        res = await mintValidatorPublicByContract();
      }
      const transReceipt = await waitForTransaction(res);
      if (transReceipt.status === "reverted") {
        throw new Error("Mint fail");
      }
      let data;
      if (validatorContractInfo?.stage === 1) {
        data = await fetchTransaction();
      } else {
        data = await fetchPublicTransaction();
      }
      console.log("mint trans", data);
      if (data.error) {
        throw new Error((data.error as any).reason);
      }
      stores.dispatch(
        actions.mint.setHistory({
          history: null,
          type: "validator",
        })
      );
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    fetchTransaction,
    fetchPublicTransaction,
    mintValidatorPublicByContract,
    mintValidatorPrivateByContract,
    switchNetworkAsync,
    chain,
    zksyncChainId,
    isLogin,
    checkWhiteList,
  ]);

  const karatRecoverMint = useCallback(async () => {
    if (!isLogin) {
      login();
      return false;
    }
    checkWhiteList();

    setMinting(true);
    try {
      if (!form.recover_tx) {
        throw new Error("miss transaction hash");
      }
      mixpanel.track("recover_mint_validator", {
        walletAddress: address as string,
        tier: 2,
        depositChainId: form.recover_chain || 1,
        depositTransactionHash: form.recover_tx,
      });
      console.log("start karat mint");
      let res;
      if (validatorContractInfo?.stage === 1) {
        res = await mintValidatorPrivate({
          walletAddress: address as string,
          depositChainId: form.recover_chain || 1,
          depositTransactionHash: form.recover_tx,
        });
      } else {
        res = await mintValidatorPublic({
          walletAddress: address as string,
          tier: 2,
          depositChainId: form.recover_chain || 1,
          depositTransactionHash: form.recover_tx,
        });
      }
      console.log("mint res", res);
      if (!res.mintId) {
        throw new Error("miss mint id");
      }
      setMintId(res.mintId);
      stores.dispatch(
        actions.mint.clearHistory({
          tx: form.recover_tx,
        })
      );
      await new Promise((resolve, reject) => {
        mintResultRef.current.timer = setInterval(() => {
          console.log("check mint", mintResultRef);
          if (mintResultRef.current.error) {
            clearInterval(mintResultRef.current.timer);
            return reject(mintResultRef.current.error.message);
          }
          if (mintResultRef.current.success) {
            clearInterval(mintResultRef.current.timer);
            return resolve(true);
          }
        }, 1000);
      });
      console.log("karat mint success");
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    karatDeposit,
    chain,
    form,
    mintHistory,
    mintStatus,
    isLogin,
    checkWhiteList,
    validatorContractInfo,
  ]);

  const karatMint = useCallback(async () => {
    if (!isLogin) {
      login();
      return false;
    }
    checkWhiteList();
    let history;
    if (mintHistory && mintStatus === "success") {
      history = mintHistory;
    } else {
      const escrowDepositInfo = form?.karat as EscrowDeposit | undefined;

      let chainId = mintHistory?.chainId || escrowDepositInfo?.token?.chainId;
      if (chain?.id !== chainId) {
        // @ts-ignore
        await switchNetworkAsync(chainId);
      }
      history = await karatDeposit();
    }
    setMinting(true);
    try {
      await new Promise((resolve) => {
        setTimeout(resolve, 3000);
      });
      console.log("start karat mint");
      let res;
      if (validatorContractInfo?.stage === 1) {
        res = await mintValidatorPrivate({
          walletAddress: address as string,
          depositChainId: history.chainId,
          depositTransactionHash: history.transaction,
        });
      } else {
        res = await mintValidatorPublic({
          walletAddress: address as string,
          tier: 2,
          depositChainId: history.chainId,
          depositTransactionHash: history.transaction,
        });
      }
      console.log("mint res", res);
      if (!res.mintId) {
        throw new Error("miss mint id");
      }
      setMintId(res.mintId);
      stores.dispatch(
        actions.mint.setHistory({
          history: null,
          type: "validator",
        })
      );
      await new Promise((resolve, reject) => {
        mintResultRef.current.timer = setInterval(() => {
          console.log("check mint", mintResultRef);
          if (mintResultRef.current.error) {
            clearInterval(mintResultRef.current.timer);
            return reject(mintResultRef.current.error.message);
          }
          if (mintResultRef.current.success) {
            clearInterval(mintResultRef.current.timer);
            return resolve(true);
          }
        }, 1000);
      });
      console.log("karat mint success");
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    karatDeposit,
    chain,
    form,
    mintHistory,
    mintStatus,
    isLogin,
    checkWhiteList,
    validatorContractInfo,
  ]);

  const zkSyncMint = useCallback(async () => {
    if (!isLogin) {
      login();
      return false;
    }
    checkWhiteList();
    const zksyncDepositInfo = form?.zksync as {
      chain: string;
      depositCost: number;
    };

    console.log("zksyncDepositInfo", zksyncDepositInfo);

    let history;
    if (mintHistory && mintStatus === "success") {
      history = mintHistory;
    } else {
      let chainId = String(mintHistory?.chainId || zksyncDepositInfo?.chain);
      if (chain?.id !== parseInt(chainId)) {
        // @ts-ignore
        await switchNetworkAsync(parseInt(chainId));
      }
      history = await zkSyncDeposit();
    }
    try {
      setMinting(true);
      await new Promise((resolve) => {
        setTimeout(resolve, 3000);
      });
      await contractMint();
    } finally {
      setMinting(false);
    }
    return true;
  }, [
    zkSyncDeposit,
    contractMint,
    form,
    switchNetworkAsync,
    mintHistory,
    mintStatus,
    isLogin,
    checkWhiteList,
  ]);

  let mintBtnText = "Deposit & Mint";
  if (mintHistory && mintStatus === "success") {
    mintBtnText = "Mint";
  }

  let defaultDepositCost = 0.2;
  if (whiteListInfo && validatorContractInfo.stage === 1) {
    defaultDepositCost = parseFloat(whiteListInfo.mintPrice);
  }

  return {
    depositSuccess,
    depositing,
    history: mintHistory,
    minting,
    karatMint,
    zkSyncMint,
    contractMint,
    karatRecoverMint,
    validatorContractInfo,
    whiteListInfo,
    mintBtnText,
    defaultDepositCost,
  };
}
